/**
 * Setting an angle on a solid figure, by sliding a point along the edge it lives on.
 *
 * ## Why not the motion a flat figure uses
 *
 * "không cần phải giữ nguyên độ dài của cạnh kéo, mà cần bảo đảm góc đó giữ nguyên mặt phẳng nó
 * đang thể hiện."
 *
 * On a page, an angle is opened by turning its arms — see `openAngle`. On a **solid** that motion
 * is not available and would be wrong if it were: the arms' points are corners and divisions of
 * the body, and a rotation on the page lifts them out of the faces they belong to. The figure
 * would keep its angle and stop being a drawing of that solid.
 *
 * What a solid figure *does* have is parameters. A point three-fifths of the way up an edge can
 * be four-fifths of the way up it; a point on a base circle can be at another angle round it.
 * Sliding one of those changes the angle and leaves the point exactly where it must be — on its
 * own edge, in its own face — so the structure is preserved by construction rather than by
 * arithmetic that could drift.
 *
 * That is also how the problem is stated: "lấy M trên SA sao cho góc AMB bằng 60°".
 *
 * ## How the parameter is found
 *
 * By sampling and then bisecting. The angle is a smooth function of the parameter but not a
 * monotone one — sliding a vertex along an edge can take the angle up and then down — so a
 * straight bisection over the whole range would miss a crossing or find the wrong one. Sampling
 * first finds every interval where the target is crossed; bisecting inside one of them is then
 * exact and cannot wander.
 *
 * Where several crossings exist, the one **nearest the parameter it already has** is taken: the
 * figure moves as little as it can, which is what keeps a drawing recognisable after an edit.
 */

/** How many samples the first pass takes. Fine enough to bracket every crossing a figure has. */
const SAMPLES = 96;

/** Bisection steps. Each halves the interval, so this is far below what a drawing can express. */
const REFINEMENTS = 40;

export interface SlideRange {
  readonly from: number;
  readonly to: number;
  /** Where the point sits now, so the nearest answer can be preferred. */
  readonly at: number;
}

/**
 * The parameter at which `angleAt` reads `target`, or `null` when it never does.
 *
 * `angleAt` returns degrees, or `null` for a parameter the figure has no answer for — a
 * degenerate position where two of the three points coincide, which has no angle rather than a
 * wrong one.
 */
export function slideToAngle(
  angleAt: (parameter: number) => number | null,
  target: number,
  range: SlideRange
): number | null {
  const span = range.to - range.from;
  if (span <= 0) return null;

  const samples: { at: number; error: number }[] = [];
  for (let step = 0; step <= SAMPLES; step += 1) {
    const at = range.from + (span * step) / SAMPLES;
    const degrees = angleAt(at);
    if (degrees !== null) samples.push({ at, error: degrees - target });
  }
  if (samples.length < 2) return null;

  /*
   * Every interval where the error changes sign, then the one whose middle is nearest where the
   * point already is. A figure that jumped to the far crossing would be a correct answer to the
   * wrong question.
   */
  let best: number | null = null;
  let bestDistance = Infinity;

  const offer = (at: number): void => {
    const distance = Math.abs(at - range.at);
    if (distance >= bestDistance) return;
    best = at;
    bestDistance = distance;
  };

  for (const [index, low] of samples.entries()) {
    /*
     * A sample that lands exactly on the target is a candidate, not an answer.
     *
     * Returning it the moment it is found was the first version, and it quietly defeated the
     * whole "nearest" rule: a curve crossing the target at 0, 0.5 and 1 always answered 0,
     * however close the point already was to the middle one.
     */
    if (low.error === 0) offer(low.at);

    const high = samples[index + 1];
    if (high === undefined) continue;
    if (low.error > 0 === high.error > 0) continue;

    const found = bisect(angleAt, target, low.at, high.at);
    if (found !== null) offer(found);
  }

  return best;
}

/** The range of angles the point can reach at all, for a message that can be acted on. */
export function reachableAngles(
  angleAt: (parameter: number) => number | null,
  range: SlideRange
): { readonly low: number; readonly high: number } | null {
  const span = range.to - range.from;
  if (span <= 0) return null;

  let low = Infinity;
  let high = -Infinity;
  for (let step = 0; step <= SAMPLES; step += 1) {
    const degrees = angleAt(range.from + (span * step) / SAMPLES);
    if (degrees === null) continue;
    low = Math.min(low, degrees);
    high = Math.max(high, degrees);
  }

  return low > high ? null : { low, high };
}

/** Halves the bracket until it is smaller than anything a drawing could show. */
function bisect(
  angleAt: (parameter: number) => number | null,
  target: number,
  from: number,
  to: number
): number | null {
  let low = from;
  let high = to;
  const at = angleAt(low);
  if (at === null) return null;
  const lowIsUnder = at - target < 0;

  for (let step = 0; step < REFINEMENTS; step += 1) {
    const middle = (low + high) / 2;
    const degrees = angleAt(middle);
    if (degrees === null) return null;

    if (degrees - target < 0 === lowIsUnder) low = middle;
    else high = middle;
  }
  return (low + high) / 2;
}
